﻿import { createContainer as baseCreateContainer } from "../src/app/container.js";

const COMPOSE_OPTION_KEYS = new Set(["container", "containerClass", "create", "overrides", "reuseExisting"]);

function ensureAgileProNamespace() {
  if (typeof window === "undefined") {
    return null;
  }
  window.AgilePro = window.AgilePro || {};
  return window.AgilePro;
}

function createContainerInstance(overrides = {}) {
  return baseCreateContainer(overrides);
}

function normaliseComposeOptions(input = {}) {
  if (!input || typeof input !== "object") {
    return { overrides: {} };
  }

  const keys = Object.keys(input);
  if (keys.some((key) => COMPOSE_OPTION_KEYS.has(key))) {
    return { ...input };
  }

  return { overrides: input };
}

function setActiveContainer(container) {
  if (typeof window !== "undefined") {
    window.__agpContainer = container;
    if (typeof window.dispatchEvent === "function") {
      try {
        if (typeof CustomEvent === "function") {
          window.dispatchEvent(new CustomEvent("agp:container-ready", { detail: { container } }));
        } else if (typeof document !== "undefined" && document.createEvent) {
          const legacyEvent = document.createEvent("CustomEvent");
          legacyEvent.initCustomEvent("agp:container-ready", false, false, { container });
          window.dispatchEvent(legacyEvent);
        }
      } catch (eventError) {
        // Swallow dispatch failures
      }
    }
    const agilePro = ensureAgileProNamespace();
    if (agilePro) {
      agilePro.__runtimeContainer = container;
    }
  }
  return container;
}

class RuntimeAppContainer {
  constructor(overrides = {}) {
    return createContainerInstance(overrides);
  }
}

export async function createRuntimeContainer(overrides = {}) {
  return createContainerInstance(overrides);
}

export function composeRuntimeContainer(input = {}) {
  const options = normaliseComposeOptions(input);
  const hasWindow = typeof window !== "undefined";

  if (hasWindow) {
    const existing = window.__agpContainer;
    if (existing && options.reuseExisting !== false) {
      return existing;
    }
  }

  if (options.container) {
    return setActiveContainer(options.container);
  }

  const overrides =
    options.overrides && typeof options.overrides === "object" && options.overrides !== null
      ? options.overrides
      : {};

  let container = null;

  if (typeof options.create === "function") {
    try {
      container = options.create(overrides) || null;
    } catch (error) {
      container = null;
    }
  }

  const ContainerCtor =
    typeof options.containerClass === "function" ? options.containerClass : RuntimeAppContainer;

  if (!container && typeof ContainerCtor === "function") {
    try {
      container = new ContainerCtor(overrides);
    } catch (error) {
      container = null;
    }
  }

  if (!container) {
    container = createContainerInstance(overrides);
  }

  return setActiveContainer(container);
}

export async function attachRuntimeContainer(input = {}) {
  return composeRuntimeContainer(input);
}

const agilePro = ensureAgileProNamespace();
if (agilePro) {
  agilePro.createRuntimeContainer = createRuntimeContainer;
  agilePro.composeContainer = composeRuntimeContainer;
  agilePro.AppContainer = RuntimeAppContainer;
}
